/**
 * KB Candidates floating widget.
 *
 * Shows pending KB candidates (auto-drafted from low-confidence inbox
 * triages plus voice-intake resolutions) in a slide-in panel. Each
 * candidate exposes:
 *   - the generalised question + current proposed_resolution
 *   - similar existing KB slugs (the auto-draft's nearest-neighbour set)
 *   - a "Refine with Avery" chat thread that can rewrite proposed_resolution
 *   - approve (POSTs to /api/support/kb with the current state then marks
 *     the candidate approved) and reject buttons
 *
 * Mounted globally from App.tsx alongside AveryWidget so it's reachable
 * from any screen — operators don't have to navigate to Support to act on
 * the queue.
 *
 * Auth: same Bearer JWT as the rest of the ops UI (verify_session_or_admin
 * on the parkify-cs side).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { callCs } from '../config/csBase'


interface RefineTurn {
  role: 'user' | 'assistant'
  text: string
  ts: string
  actor?: string
}

interface KbCandidate {
  id: number
  source: string
  source_id: string
  intent: string | null
  question: string
  proposed_resolution: string
  status: 'pending' | 'approved' | 'rejected'
  reviewer: string | null
  reviewed_at: string | null
  created_at: string
  caller_name: string | null
  caller_email: string | null
  plate: string | null
  issue_category: string | null
  transcription_text: string | null
  similar_slugs: string[] | null
  confidence_was: number | null
  refine_history: RefineTurn[] | null
  // research_agent source: sources the reviewer verifies before promoting.
  citations?: Array<{ url: string; title?: string; published?: string; snippet?: string }> | null
}

const PURPLE = '#7F3F98'
const POLL_MS = 60_000

// Citation URLs come from the external research agent — only render http(s) as a
// clickable link so a `javascript:`/`data:` scheme can't XSS a staff reviewer.
function isHttpUrl(u: string): boolean {
  try {
    const p = new URL(u)
    return p.protocol === 'http:' || p.protocol === 'https:'
  } catch {
    return false
  }
}

export default function KbCandidatesWidget() {
  const { authHeaders, user } = useAuth()
  const [open, setOpen] = useState(false)
  const [pendingCount, setPendingCount] = useState(0)
  const [items, setItems] = useState<KbCandidate[]>([])
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState<'pending' | 'all'>('pending')
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const headers = useMemo(() => authHeaders, [authHeaders])
  // Guard: don't fetch until the user is logged in (authHeaders returns {}).
  const isAuthed = !!user?.email

  // Poll pending count for the badge so operators see new candidates without
  // re-opening the panel.
  useEffect(() => {
    if (!isAuthed) return
    let cancelled = false
    const tick = async () => {
      try {
        const res = await callCs(`/api/support/kb-candidates?status=pending&limit=1`, {
          headers: headers() as Record<string, string>,
        })
        if (!res.ok) return
        const data = await res.json() as { pending_count?: number }
        if (!cancelled) setPendingCount(data.pending_count ?? 0)
      } catch { /* network blip — keep last count */ }
    }
    void tick()
    const id = setInterval(tick, POLL_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [isAuthed, headers])

  const load = async () => {
    if (!isAuthed) return
    setLoading(true); setError(null)
    try {
      const res = await callCs(`/api/support/kb-candidates?status=${filter}`, {
        headers: headers() as Record<string, string>,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json() as { items: KbCandidate[]; pending_count: number }
      setItems(data.items ?? [])
      setPendingCount(data.pending_count ?? 0)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open) void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filter])

  const updateItem = (id: number, patch: Partial<KbCandidate>) => {
    setItems(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c))
  }

  if (!isAuthed) return null

  return (
    <>
      {/* Floating launcher. Was offset to bottom:90 to clear the Ask Avery
          button below it; that launcher was removed 2026-08-25, so this one
          drops to the corner rather than hovering over a gap. */}
      <button
        className="kb-candidates-launcher"
        onClick={() => setOpen(o => !o)}
        title="KB Candidates"
        style={{
          position: 'fixed', bottom: 16, right: 16, zIndex: 1100,
          width: 56, height: 56, borderRadius: 28,
          background: PURPLE, color: 'white',
          border: 'none', cursor: 'pointer',
          boxShadow: '0 6px 16px rgba(127,63,152,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'inherit',
        }}
      >
        <span style={{ fontSize: 22 }}>🪄</span>
        {pendingCount > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            minWidth: 22, height: 22, borderRadius: 11,
            background: '#dc2626', color: 'white',
            fontSize: 11, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 6px',
            border: '2px solid white',
          }}>
            {pendingCount > 99 ? '99+' : pendingCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: 'min(520px, 100vw)', zIndex: 1099,
          background: 'var(--color-white)',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.12)',
          display: 'flex', flexDirection: 'column',
          fontFamily: 'inherit',
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--color-gray-200)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>🪄 KB Candidates</div>
              <div style={{ fontSize: 11, color: 'var(--color-gray-500)' }}>
                Refine + approve auto-drafted KB entries from low-confidence triages.
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                width: 28, height: 28, borderRadius: 6,
                background: 'transparent', border: '1px solid var(--color-gray-300)',
                color: 'var(--color-gray-600)', cursor: 'pointer',
                fontSize: 16, lineHeight: 1, fontFamily: 'inherit',
              }}
            >×</button>
          </div>

          {/* Filter row */}
          <div style={{
            padding: '10px 18px', display: 'flex', gap: 6, alignItems: 'center',
            borderBottom: '1px solid var(--color-gray-200)',
            background: 'var(--color-gray-100)',
          }}>
            {(['pending', 'all'] as const).map(f => (
              <button
                key={f}
                onClick={() => { setFilter(f); setExpandedId(null) }}
                style={{
                  fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 'var(--radius-chip)',
                  background: filter === f ? PURPLE : 'transparent',
                  color: filter === f ? 'white' : 'var(--color-gray-700)',
                  border: `1px solid ${filter === f ? PURPLE : 'var(--color-gray-300)'}`,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >{f === 'pending' ? `Pending (${pendingCount})` : 'All'}</button>
            ))}
            <button
              onClick={() => void load()}
              style={{
                marginLeft: 'auto',
                fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 'var(--radius-chip)',
                background: 'transparent', color: 'var(--color-gray-700)',
                border: '1px solid var(--color-gray-300)', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >↻ Refresh</button>
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto', background: 'var(--color-gray-100)' }}>
            {loading && <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--color-gray-500)' }}>Loading…</div>}
            {error && (
              <div style={{ margin: 12, padding: 10, borderRadius: 8, background: 'rgba(237,28,36,0.08)', border: '1px solid rgba(237,28,36,0.25)', color: '#b91c1c', fontSize: 12 }}>
                {error}
              </div>
            )}
            {!loading && !error && items.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--color-gray-500)' }}>
                No {filter === 'pending' ? 'pending' : ''} candidates. Low-confidence triages will land here automatically.
              </div>
            )}
            {!loading && items.map(c => (
              <CandidateCard
                key={c.id}
                candidate={c}
                expanded={expandedId === c.id}
                onToggle={() => setExpandedId(prev => prev === c.id ? null : c.id)}
                onUpdate={patch => updateItem(c.id, patch)}
                onRemoveFromPending={() => {
                  setItems(prev => prev.filter(x => x.id !== c.id))
                  setPendingCount(p => Math.max(0, p - 1))
                }}
                authHeaders={headers}
              />
            ))}
          </div>
        </div>
      )}
    </>
  )
}


// ─── single candidate card ─────────────────────────────────────────────────

function CandidateCard({
  candidate,
  expanded,
  onToggle,
  onUpdate,
  onRemoveFromPending,
  authHeaders,
}: {
  candidate: KbCandidate
  expanded: boolean
  onToggle: () => void
  onUpdate: (patch: Partial<KbCandidate>) => void
  onRemoveFromPending: () => void
  authHeaders: () => HeadersInit
}) {
  const [draftMessage, setDraftMessage] = useState('')
  const [refining, setRefining] = useState(false)
  const [acting, setActing] = useState<'approve' | 'reject' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const chatEndRef = useRef<HTMLDivElement>(null)

  // Editable resolution — initialised from the row, overwritten by chat
  // refinements. Operator can also hand-edit before approving.
  const [resolutionDraft, setResolutionDraft] = useState(candidate.proposed_resolution)
  useEffect(() => { setResolutionDraft(candidate.proposed_resolution) }, [candidate.proposed_resolution])

  useEffect(() => {
    if (expanded) chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [expanded, candidate.refine_history])

  const sendRefine = async () => {
    const msg = draftMessage.trim()
    if (!msg) return
    setRefining(true); setError(null)
    try {
      const res = await callCs(`/api/support/kb-candidates/${candidate.id}/refine`, {
        method: 'POST',
        headers: { ...(authHeaders() as Record<string, string>), 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg }),
      })
      if (!res.ok) {
        const txt = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status}${txt ? `: ${txt.slice(0, 200)}` : ''}`)
      }
      const data = await res.json() as {
        reply: string
        updated_proposed_resolution: string | null
        refine_history: RefineTurn[]
      }
      onUpdate({
        refine_history: data.refine_history,
        proposed_resolution: data.updated_proposed_resolution ?? candidate.proposed_resolution,
      })
      if (data.updated_proposed_resolution) {
        setResolutionDraft(data.updated_proposed_resolution)
      }
      setDraftMessage('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setRefining(false)
    }
  }

  const approve = async () => {
    // Prompt for a slug suggestion derived from the question. The operator
    // can edit it before we POST to KB.
    const defaultSlug = slugify(candidate.question)
    const slug = window.prompt('KB slug for this entry?', defaultSlug)?.trim()
    if (!slug) return
    const title = candidate.question.length > 80
      ? candidate.question.slice(0, 77) + '…'
      : candidate.question
    setActing('approve'); setError(null)
    try {
      // 1. Save to KB
      const kbRes = await callCs(`/api/support/kb`, {
        method: 'POST',
        headers: { ...(authHeaders() as Record<string, string>), 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: slug, title, content: resolutionDraft }),
      })
      if (!kbRes.ok) {
        const txt = await kbRes.text().catch(() => '')
        throw new Error(`KB save failed: HTTP ${kbRes.status}${txt ? `: ${txt.slice(0, 200)}` : ''}`)
      }
      // 2. Mark candidate approved
      const apRes = await callCs(`/api/support/kb-candidates/${candidate.id}/approve`, {
        method: 'POST',
        headers: { ...(authHeaders() as Record<string, string>), 'Content-Type': 'application/json' },
        body: JSON.stringify({ kb_slug: slug }),
      })
      if (!apRes.ok) throw new Error(`approve failed: HTTP ${apRes.status}`)
      onRemoveFromPending()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setActing(null)
    }
  }

  const reject = async () => {
    const reason = window.prompt('Reason for rejection (optional):') ?? ''
    setActing('reject'); setError(null)
    try {
      const res = await callCs(`/api/support/kb-candidates/${candidate.id}/reject`, {
        method: 'POST',
        headers: { ...(authHeaders() as Record<string, string>), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || null }),
      })
      if (!res.ok) throw new Error(`reject failed: HTTP ${res.status}`)
      onRemoveFromPending()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setActing(null)
    }
  }

  const created = relativeTime(candidate.created_at)

  return (
    <div style={{
      margin: '8px 12px', background: 'var(--color-white)',
      borderRadius: 10, border: '1px solid var(--color-gray-200)',
      overflow: 'hidden',
    }}>
      <button
        onClick={onToggle}
        style={{
          display: 'block', width: '100%', textAlign: 'left',
          background: expanded ? 'rgba(127,63,152,0.04)' : 'transparent',
          border: 'none', cursor: 'pointer', padding: '12px 14px',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>
            {candidate.question.slice(0, 120)}{candidate.question.length > 120 ? '…' : ''}
          </div>
          <span style={{ fontSize: 10, color: 'var(--color-gray-500)' }}>{created}</span>
        </div>
        <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {candidate.intent && (
            <span style={{ fontSize: 10, fontWeight: 700, color: PURPLE, background: 'rgba(127,63,152,0.1)', padding: '2px 7px', borderRadius: 999 }}>
              {candidate.intent}
            </span>
          )}
          <span style={{ fontSize: 10, color: 'var(--color-gray-500)' }}>
            via {candidate.source === 'email_thread' ? '📧 inbox' : candidate.source === 'voice_intake' ? '📞 voice' : candidate.source === 'research_agent' ? '🔍 research' : candidate.source}
          </span>
          {candidate.confidence_was != null && (
            <span style={{ fontSize: 10, color: 'var(--color-gray-500)' }}>
              conf {(candidate.confidence_was * 100).toFixed(0)}%
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div style={{ padding: '12px 14px 14px', borderTop: '1px solid var(--color-gray-200)' }}>
          {error && (
            <div style={{ marginBottom: 10, padding: 8, borderRadius: 6, background: 'rgba(237,28,36,0.08)', border: '1px solid rgba(237,28,36,0.25)', color: '#b91c1c', fontSize: 11 }}>
              {error}
            </div>
          )}

          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.4px', textTransform: 'uppercase', color: 'var(--color-gray-500)', marginBottom: 4 }}>Proposed resolution</div>
          <textarea
            value={resolutionDraft}
            onChange={e => setResolutionDraft(e.target.value)}
            rows={5}
            style={{
              width: '100%', fontFamily: 'inherit', fontSize: 12, lineHeight: 1.5,
              padding: 8, borderRadius: 6, border: '1px solid var(--color-gray-300)',
              boxSizing: 'border-box', resize: 'vertical',
            }}
          />

          {(candidate.similar_slugs?.length ?? 0) > 0 && (
            <div style={{ marginTop: 10, fontSize: 11, color: 'var(--color-gray-500)' }}>
              Similar slugs: {candidate.similar_slugs!.map((s, i) => (
                <span key={s}>
                  {i > 0 && ', '}
                  <code style={{ fontFamily: 'monospace', color: PURPLE }}>{s}</code>
                </span>
              ))}
            </div>
          )}

          {(candidate.citations?.length ?? 0) > 0 && (
            <div style={{ marginTop: 10, fontSize: 11, color: 'var(--color-gray-500)' }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                🔍 Research citations — verify before approving
              </div>
              {candidate.citations!.map((c, i) => (
                <div key={i} style={{ marginBottom: 6, paddingLeft: 8, borderLeft: '2px solid rgba(127,63,152,0.25)' }}>
                  {isHttpUrl(c.url) ? (
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: PURPLE, textDecoration: 'none', fontWeight: 600, wordBreak: 'break-word' }}
                    >
                      {c.title || c.url}
                    </a>
                  ) : (
                    <span style={{ color: 'var(--color-gray-700)', fontWeight: 600, wordBreak: 'break-word' }}>
                      {c.title || c.url}
                    </span>
                  )}
                  {c.published && (
                    <span style={{ fontSize: 10, color: 'var(--color-gray-500)', marginLeft: 6 }}>{c.published}</span>
                  )}
                  {c.snippet && (
                    <div style={{ fontSize: 10, color: 'var(--color-gray-600)', marginTop: 2, maxHeight: 60, overflow: 'hidden' }}>
                      {c.snippet}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {candidate.transcription_text && (
            <details style={{ marginTop: 10, fontSize: 11 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--color-gray-600)' }}>Source transcript</summary>
              <div style={{ marginTop: 6, padding: 8, background: 'var(--color-gray-100)', borderRadius: 6, whiteSpace: 'pre-wrap', maxHeight: 180, overflowY: 'auto' }}>
                {candidate.transcription_text}
              </div>
            </details>
          )}

          {/* Refine chat */}
          <div style={{ marginTop: 14, padding: 10, borderRadius: 8, background: 'var(--color-gray-100)' }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.4px', textTransform: 'uppercase', color: 'var(--color-gray-500)', marginBottom: 8 }}>
              💬 Refine with Avery
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 8 }}>
              {(candidate.refine_history ?? []).length === 0 && (
                <div style={{ fontSize: 11, color: 'var(--color-gray-500)', fontStyle: 'italic' }}>
                  No turns yet. Ask Avery to broaden, narrow, or rewrite the resolution.
                </div>
              )}
              {(candidate.refine_history ?? []).map((t, i) => (
                <div key={i} style={{
                  margin: '6px 0', padding: '6px 10px', borderRadius: 8,
                  background: t.role === 'user' ? 'rgba(127,63,152,0.08)' : 'var(--color-white)',
                  border: t.role === 'assistant' ? '1px solid var(--color-gray-200)' : 'none',
                  fontSize: 12, lineHeight: 1.5,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: t.role === 'user' ? PURPLE : '#15803d', marginBottom: 2 }}>
                    {t.role === 'user' ? (t.actor ?? 'You') : 'Avery'}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{t.text}</div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <textarea
                value={draftMessage}
                onChange={e => setDraftMessage(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault(); void sendRefine()
                  }
                }}
                placeholder="e.g. 'Generalise this for any temporary event zone' (⌘/Ctrl+Enter to send)"
                rows={2}
                style={{
                  flex: 1, fontFamily: 'inherit', fontSize: 12, lineHeight: 1.5,
                  padding: 8, borderRadius: 6, border: '1px solid var(--color-gray-300)',
                  boxSizing: 'border-box', resize: 'vertical',
                }}
              />
              <button
                onClick={() => void sendRefine()}
                disabled={refining || !draftMessage.trim()}
                style={{
                  padding: '8px 14px', borderRadius: 'var(--radius-btn)',
                  background: PURPLE, color: 'white', border: 'none',
                  fontWeight: 700, fontSize: 12, cursor: refining || !draftMessage.trim() ? 'not-allowed' : 'pointer',
                  opacity: refining || !draftMessage.trim() ? 0.5 : 1,
                  fontFamily: 'inherit',
                }}
              >{refining ? '…' : 'Send'}</button>
            </div>
          </div>

          {/* Approve / Reject */}
          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            <button
              onClick={() => void approve()}
              disabled={acting !== null}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 'var(--radius-btn)',
                background: '#15803d', color: 'white', border: 'none',
                fontWeight: 700, fontSize: 12, cursor: acting ? 'not-allowed' : 'pointer',
                opacity: acting ? 0.5 : 1, fontFamily: 'inherit',
              }}
            >
              {acting === 'approve' ? 'Saving…' : '✓ Approve & save to KB'}
            </button>
            <button
              onClick={() => void reject()}
              disabled={acting !== null}
              style={{
                padding: '10px 14px', borderRadius: 'var(--radius-btn)',
                background: 'transparent', color: '#b91c1c',
                border: '1px solid rgba(237,28,36,0.4)',
                fontWeight: 700, fontSize: 12, cursor: acting ? 'not-allowed' : 'pointer',
                opacity: acting ? 0.5 : 1, fontFamily: 'inherit',
              }}
            >
              {acting === 'reject' ? '…' : 'Reject'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}


// ─── helpers ───────────────────────────────────────────────────────────────

function slugify(s: string): string {
  return s.toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'kb-entry'
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
